import React, { useState, useEffect, useMemo } from 'react';
import { fetchEmails } from '../utils/api';
import { timeAgo, normalizeImportance } from '../utils/formatters';
import {
  Zap,
  CheckSquare,
  Inbox,
  Search,
  Loader2,
  Rocket,
  AlertTriangle,
  Sparkles,
  Gauge,
  RefreshCw,
  Cpu,
  Layers,
  Shield,
  Flame,
  Star,
  ArrowUpRight,
  Activity,
  Clock3,
  TrendingUp,
} from 'lucide-react';

const ROTATING_TIPS = [
  'Already-analyzed threads are skipped so you never pay twice for the same Gmail id.',
  'Free includes 15 AI runs per month — upgrade in Settings → Billing for hundreds more.',
  'Elite tier is built for 24/7 teams with ~4× the Pro allowance and higher burst limits.',
  'Abuse protection watches for credit-burn patterns; play fair and you will never see a strike.',
  'Turn on 2FA under Settings → Security to guard your account if you step away.',
  'Use “Select Specific” + search to laser-focus on a sender before spending quota.',
];

function greetingByHour() {
  const h = new Date().getHours();
  if (h < 5) return 'Working late';
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export default function EmailSelector({
  onAnalyze,
  analyzedEmailIds = new Set(),
  account = null,
  onRefreshAccount,
  showTips = true,
  maxBatchCap = 500,
  sessions = [],
  onOpenSession,
  onOpenInbox,
  userEmail,
}) {
  const [mode, setMode] = useState('recent');
  const [folder, setFolder] = useState('inbox');
  const recentMax = Math.max(5, Math.min(200, maxBatchCap));
  const bulkMax = Math.max(5, Math.min(500, maxBatchCap));
  const [count, setCount] = useState(() => Math.min(20, recentMax));
  const [emails, setEmails] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [tipIdx, setTipIdx] = useState(0);

  useEffect(() => {
    setCount((c) => Math.min(c, recentMax));
  }, [recentMax]);

  useEffect(() => {
    if (!showTips) return undefined;
    const id = setInterval(() => {
      setTipIdx((i) => (i + 1) % ROTATING_TIPS.length);
    }, 5200);
    return () => clearInterval(id);
  }, [showTips]);

  const loadEmails = async (num) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchEmails(num || 100, folder);
      setEmails(data);
      setLoaded(true);
    } catch (e) {
      console.error('Failed to load emails:', e);
      setError('Failed to load emails. Please try again.');
    }
    setLoading(false);
  };

  useEffect(() => {
    if ((mode === 'select' || mode === 'all') && !loaded && !loading && !error) {
      loadEmails(Math.min(200, bulkMax));
    }
  }, [mode, loaded, loading, error, folder, bulkMax]);

  useEffect(() => {
    setLoaded(false);
    setEmails([]);
    setSelected(new Set());
  }, [folder]);

  const toggleSelect = (id) => {
    if (analyzedEmailIds.has(id)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalFetched = emails.length;
  const freshEmails = emails.filter((e) => !analyzedEmailIds.has(e.id));
  const hiddenAnalyzed = totalFetched - freshEmails.length;

  const filteredEmails = freshEmails.filter((e) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      e.subject?.toLowerCase().includes(q) ||
      e.sender?.toLowerCase().includes(q) ||
      e.snippet?.toLowerCase().includes(q)
    );
  });

  const selectableFiltered = filteredEmails;
  const allFilteredSelected =
    selectableFiltered.length > 0 && selectableFiltered.every((e) => selected.has(e.id));

  const selectAll = () => {
    if (allFilteredSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        filteredEmails.forEach((e) => next.delete(e.id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        selectableFiltered.forEach((e) => next.add(e.id));
        return next;
      });
    }
  };

  const handleAnalyzeRecent = async () => {
    setLoading(true);
    setError(null);
    try {
      const target = Math.min(count, recentMax);
      const overhead = Math.min(bulkMax, target * 2 + 20);
      const data = await fetchEmails(overhead, folder);
      const ids = data
        .map((e) => e.id)
        .filter((id) => !analyzedEmailIds.has(id))
        .slice(0, target);
      if (ids.length === 0) {
        setError('No fresh messages to analyze in this folder — all of them are already in your saved runs.');
        setLoading(false);
        return;
      }
      onAnalyze(ids);
    } catch (e) {
      console.error(e);
      setError('Failed to fetch recent emails.');
    }
    setLoading(false);
  };

  const handleAnalyzeSelected = () => {
    const ids = Array.from(selected);
    onAnalyze(ids);
  };

  const handleAnalyzeAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchEmails(bulkMax, folder);
      const ids = data.map((e) => e.id).filter((id) => !analyzedEmailIds.has(id));
      if (ids.length === 0) {
        setError('All fetched messages are already analyzed. Nothing new to run.');
        setLoading(false);
        return;
      }
      onAnalyze(ids);
    } catch (e) {
      console.error(e);
      setError('Failed to fetch all emails.');
    }
    setLoading(false);
  };

  const rem = account?.messages_remaining ?? null;
  const lim = account?.monthly_limit ?? null;
  const tier = account?.tier ?? 'free';
  const quotaPct = rem != null && lim ? Math.min(100, Math.round((rem / Math.max(lim, 1)) * 100)) : null;

  const tierLabel = useMemo(() => {
    if (tier === 'elite') return 'Elite 24/7';
    if (tier === 'pro') return 'Pro';
    return 'Free';
  }, [tier]);

  const allResults = useMemo(() => {
    const out = [];
    for (const s of sessions || []) {
      for (const r of s.results || []) {
        out.push({ ...r, _sessionId: s.id, _sessionTitle: s.title, _sessionCreated: s.createdAt });
      }
    }
    return out;
  }, [sessions]);

  const stats = useMemo(() => {
    const c = { critical: 0, important: 0, routine: 0, low: 0 };
    for (const r of allResults) {
      const t = normalizeImportance(r.importance);
      c[t] = (c[t] || 0) + 1;
    }
    const total = allResults.length;
    return { ...c, total, sessions: sessions.length };
  }, [allResults, sessions.length]);

  const recentRuns = useMemo(() => {
    const arr = [...(sessions || [])];
    arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return arr.slice(0, 3);
  }, [sessions]);

  const spotlight = useMemo(() => {
    const ranked = [...allResults].sort((a, b) => {
      const order = { critical: 0, important: 1, routine: 2, low: 3 };
      return (
        (order[normalizeImportance(a.importance)] ?? 9) -
        (order[normalizeImportance(b.importance)] ?? 9) ||
        new Date(b.email?.date || 0) - new Date(a.email?.date || 0)
      );
    });
    return ranked.slice(0, 3);
  }, [allResults]);

  const distroPct = useMemo(() => {
    const total = Math.max(1, stats.total);
    return {
      critical: Math.round((stats.critical / total) * 100),
      important: Math.round((stats.important / total) * 100),
      routine: Math.round((stats.routine / total) * 100),
      low: Math.round((stats.low / total) * 100),
    };
  }, [stats]);

  return (
    <div className="analysis-page">
      <section className="analysis-hero glass anim-rise">
        <div className="analysis-hero-grid">
          <div className="analysis-hero-copy anim-stagger-item">
            <p className="analysis-kicker">
              <Sparkles size={14} /> New analysis
            </p>
            <h1 className="analysis-title">Turn your inbox into signal</h1>
            <p className="analysis-lead">
              AI reads each thread, assigns importance, and writes a crisp summary — tuned for how you work.
            </p>
            <div className="analysis-chips">
              <span className="analysis-chip">
                <Cpu size={14} /> Gemini-powered
              </span>
              <span className="analysis-chip">
                <Layers size={14} /> Skips duplicates
              </span>
              <span className="analysis-chip">
                <Shield size={14} /> Quota-aware
              </span>
            </div>
          </div>
          <div className="analysis-hero-stats anim-stagger-item">
            <div className="analysis-stat-card glass-elevated">
              <Gauge size={22} className="analysis-stat-icon" />
              <div className="analysis-stat-label">Plan</div>
              <div className="analysis-stat-value">{tierLabel}</div>
            </div>
            <div className="analysis-stat-card glass-elevated">
              <Zap size={22} className="analysis-stat-icon" />
              <div className="analysis-stat-label">Saved in browser</div>
              <div className="analysis-stat-value">{analyzedEmailIds.size}</div>
            </div>
            {quotaPct != null && (
              <div className="analysis-quota-block">
                <div className="analysis-quota-top">
                  <span>Monthly AI pool</span>
                  <span className="mono">
                    {rem} / {lim}
                  </span>
                </div>
                <div className="analysis-quota-bar">
                  <div className="analysis-quota-fill" style={{ width: `${quotaPct}%` }} />
                </div>
                <button type="button" className="btn btn-sm analysis-refresh" onClick={() => onRefreshAccount?.()}>
                  <RefreshCw size={14} /> Sync usage
                </button>
              </div>
            )}
          </div>
        </div>
        {showTips && (
          <div className="analysis-tip glass-elevated anim-fade" key={tipIdx}>
            <Sparkles size={16} className="analysis-tip-icon" />
            <p>{ROTATING_TIPS[tipIdx]}</p>
          </div>
        )}
      </section>

      {stats.total > 0 && (
        <section className="inbox-pulse-grid anim-rise">
          <div className="inbox-greet glass">
            <div className="inbox-greet-time">
              <Clock3 size={14} /> {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
            </div>
            <h2 className="inbox-greet-title">
              {greetingByHour()}{userEmail ? `, ${userEmail.split('@')[0]}` : ''}
            </h2>
            <p className="inbox-greet-sub">
              You have <strong>{stats.total}</strong> analyzed message{stats.total !== 1 ? 's' : ''} across{' '}
              <strong>{stats.sessions}</strong> run{stats.sessions !== 1 ? 's' : ''}. Let's see what needs attention.
            </p>

            <div className="inbox-mini-stats">
              <div className="inbox-mini-stat tier-critical">
                <Flame size={18} />
                <div>
                  <div className="inbox-mini-num">{stats.critical}</div>
                  <div className="inbox-mini-lbl">Critical</div>
                </div>
              </div>
              <div className="inbox-mini-stat tier-important">
                <Zap size={18} />
                <div>
                  <div className="inbox-mini-num">{stats.important}</div>
                  <div className="inbox-mini-lbl">Important</div>
                </div>
              </div>
              <div className="inbox-mini-stat tier-routine">
                <Activity size={18} />
                <div>
                  <div className="inbox-mini-num">{stats.routine}</div>
                  <div className="inbox-mini-lbl">Routine</div>
                </div>
              </div>
              <div className="inbox-mini-stat tier-low">
                <TrendingUp size={18} />
                <div>
                  <div className="inbox-mini-num">{stats.low}</div>
                  <div className="inbox-mini-lbl">Low</div>
                </div>
              </div>
            </div>

            <div className="inbox-distro" aria-label="Importance distribution">
              <div className="inbox-distro-bar">
                <span className="seg critical" style={{ width: `${distroPct.critical}%` }} />
                <span className="seg important" style={{ width: `${distroPct.important}%` }} />
                <span className="seg routine" style={{ width: `${distroPct.routine}%` }} />
                <span className="seg low" style={{ width: `${distroPct.low}%` }} />
              </div>
              <div className="inbox-distro-legend">
                <span><i className="dot critical" /> {distroPct.critical}%</span>
                <span><i className="dot important" /> {distroPct.important}%</span>
                <span><i className="dot routine" /> {distroPct.routine}%</span>
                <span><i className="dot low" /> {distroPct.low}%</span>
              </div>
            </div>
          </div>

          <div className="inbox-side-col">
            <div className="inbox-spotlight glass-elevated">
              <div className="inbox-spotlight-head">
                <Star size={16} /> Spotlight
                {onOpenSession && recentRuns[0] && (
                  <button
                    type="button"
                    className="inbox-link-btn"
                    onClick={() => onOpenSession?.(recentRuns[0].id)}
                  >
                    Open run <ArrowUpRight size={12} />
                  </button>
                )}
              </div>
              {spotlight.length === 0 ? (
                <p className="inbox-empty">No analyzed threads yet — start one below.</p>
              ) : (
                <ul className="inbox-spotlight-list">
                  {spotlight.map((r, i) => {
                    const t = normalizeImportance(r.importance);
                    return (
                      <li
                        key={(r.email?.id || '') + i}
                        className={`spot-item tier-${t}`}
                        onClick={() => r._sessionId && onOpenSession?.(r._sessionId)}
                        role={r._sessionId ? 'button' : undefined}
                        tabIndex={r._sessionId ? 0 : -1}
                      >
                        <span className={`spot-tier tier-${t}`} aria-hidden />
                        <div className="spot-body">
                          <div className="spot-subject" title={r.email?.subject}>
                            {r.email?.subject || '(No subject)'}
                          </div>
                          <div className="spot-meta">
                            {r.email?.sender || 'Unknown'} · {timeAgo(r.email?.date)}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="inbox-recent glass-elevated">
              <div className="inbox-recent-head">
                <Layers size={14} /> Recent runs
              </div>
              {recentRuns.length === 0 ? (
                <p className="inbox-empty">No saved runs yet.</p>
              ) : (
                <ul className="inbox-recent-list">
                  {recentRuns.map((s) => (
                    <li
                      key={s.id}
                      onClick={() => onOpenSession?.(s.id)}
                      role="button"
                      tabIndex={0}
                      className="recent-run-row"
                    >
                      <div className="recent-run-title">{s.title}</div>
                      <div className="recent-run-meta">
                        {s.results?.length || 0} email{(s.results?.length || 0) !== 1 ? 's' : ''} ·{' '}
                        {timeAgo(s.createdAt)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}

      <div className="mode-tabs anim-fade">
        <button
          type="button"
          className={`mode-tab ${mode === 'recent' ? 'active' : ''}`}
          onClick={() => setMode('recent')}
        >
          <Zap size={16} /> Most Recent
        </button>
        <button
          type="button"
          className={`mode-tab ${mode === 'select' ? 'active' : ''}`}
          onClick={() => setMode('select')}
        >
          <CheckSquare size={16} /> Select Specific
        </button>
        <button
          type="button"
          className={`mode-tab ${mode === 'all' ? 'active' : ''}`}
          onClick={() => setMode('all')}
        >
          <Inbox size={16} /> Bulk ({bulkMax})
        </button>
      </div>

      <div className="folder-row anim-fade">
        <label className="folder-label">Folder</label>
        <select className="folder-select" value={folder} onChange={(e) => setFolder(e.target.value)}>
          <option value="inbox">Inbox</option>
          <option value="sent">Sent</option>
          <option value="drafts">Drafts</option>
          <option value="starred">Starred</option>
          <option value="snoozed">Snoozed</option>
        </select>
        <span className="folder-cap-hint">Batch cap from settings: {bulkMax}</span>
      </div>

      <div className="selector-panel glass anim-rise">
        {error && (
          <div className="warning-card analysis-error">
            <AlertTriangle size={18} /> {error}
          </div>
        )}

        {mode === 'recent' && (
          <>
            <div className="count-control">
              <span className="count-label">Emails to analyze</span>
              <input
                type="range"
                min="5"
                max={recentMax}
                value={Math.min(count, recentMax)}
                onChange={(e) => setCount(Number(e.target.value))}
              />
              <div className="count-display">{Math.min(count, recentMax)}</div>
            </div>
            <button type="button" className="btn btn-primary btn-glow-pulse" onClick={handleAnalyzeRecent} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 size={16} className="btn-spinner" /> Loading…
                </>
              ) : (
                <>
                  <Rocket size={16} /> Analyze {Math.min(count, recentMax)} emails
                </>
              )}
            </button>
          </>
        )}

        {mode === 'select' && (
          <>
            <div className="search-wrap">
              <Search size={16} className="search-ico" />
              <input
                type="text"
                className="search-input"
                style={{ paddingLeft: 44, marginBottom: 0 }}
                placeholder="Search subject, sender, or snippet…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {loading && !loaded ? (
              <div className="loading-center">
                <div className="loading-spinner" />
                <span>Loading emails…</span>
              </div>
            ) : (
              <>
                <div className="select-toolbar">
                  <span className="select-meta">
                    {selected.size} selected
                    {hiddenAnalyzed > 0 ? (
                      <span className="select-meta-sub">
                        · {hiddenAnalyzed} already analyzed (hidden)
                      </span>
                    ) : null}
                  </span>
                  <button type="button" className="btn btn-sm" onClick={selectAll} disabled={selectableFiltered.length === 0}>
                    {allFilteredSelected ? 'Deselect visible' : 'Select all visible'}
                  </button>
                </div>
                <div className="email-list-select">
                  {filteredEmails.map((e, idx) => (
                    <label
                      key={e.id}
                      className="email-check-item anim-stagger-item"
                      style={{ animationDelay: `${idx * 0.02}s` }}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(e.id)}
                        onChange={() => toggleSelect(e.id)}
                      />
                      <div className="email-check-info">
                        <div className="email-check-subject">{e.subject}</div>
                        <div className="email-check-meta">
                          {e.sender} · {timeAgo(e.date)}
                        </div>
                      </div>
                    </label>
                  ))}
                  {filteredEmails.length === 0 && !loading && (
                    <div className="empty-list">
                      {hiddenAnalyzed > 0
                        ? 'Nothing fresh in this folder — every message here is already in your saved runs.'
                        : 'No emails in this view.'}
                    </div>
                  )}
                </div>
                <button type="button" className="btn btn-primary btn-glow-pulse" onClick={handleAnalyzeSelected} disabled={selected.size === 0}>
                  <Rocket size={16} /> Analyze {selected.size} email{selected.size !== 1 ? 's' : ''}
                </button>
              </>
            )}
          </>
        )}

        {mode === 'all' && (
          <>
            <div className="warning-card bulk-hint">
              <AlertTriangle size={20} />
              <div>
                Fetches up to <strong>{bulkMax}</strong> messages from the selected folder (your settings cap). Processing
                stops if you hit your monthly AI quota. You can cancel anytime.
              </div>
            </div>
            <button type="button" className="btn btn-primary btn-glow-pulse" onClick={handleAnalyzeAll} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 size={16} className="btn-spinner" /> Loading…
                </>
              ) : (
                <>
                  <Rocket size={16} /> Analyze up to {bulkMax} emails
                </>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
